import { Updater, useImmer } from "use-immer";
import { createContext, ReactNode, useCallback, useContext } from "react";

import ChildrenOrOutlet from "components/ChildrenOrOutlet";
import { useFeatureSwitch } from "components/FeatureSwitch";
import { FeatureSwitch } from "components/FeatureSwitch/FeatureSwitches";

import { getCookie, ScraperAPICookies, setCookie } from "utils/cookieUtils";


export type PilotFeatureSetting = "yes" | "no" | "never";

type PilotFeaturesContextType = {
  pilotFeatures: Partial<Record<FeatureSwitch, PilotFeatureSetting>>;
  setPilotFeatures: Updater<Partial<Record<FeatureSwitch, PilotFeatureSetting>>>;
};

let PilotFeaturesContext = createContext<PilotFeaturesContextType>(null!);


export default function PilotFeaturesProvider({ children }: { children: ReactNode }) {

  const [ pilotFeatures, setPilotFeatures ] = useImmer<Partial<Record<FeatureSwitch, PilotFeatureSetting>>>({});


  return <PilotFeaturesContext.Provider value={ {
    pilotFeatures: pilotFeatures,
    setPilotFeatures: setPilotFeatures
  } }>
    <ChildrenOrOutlet children={ children }/>
  </PilotFeaturesContext.Provider>;
};


export function usePilotFeature(
  {
    featureSwitch,
    cookieName
  }: {
    featureSwitch: FeatureSwitch;
    cookieName?: ScraperAPICookies;
  }
) {
  const { pilotFeatures, setPilotFeatures } = useContext(PilotFeaturesContext);

  const pilotFeatureValue = pilotFeatures[featureSwitch] ?? (cookieName ? getCookie(cookieName) as PilotFeatureSetting: undefined);
  const pilotFeatureCanBeUsed = useFeatureSwitch(featureSwitch);


  const updatePilotFeature = useCallback((newValue: PilotFeatureSetting) => {
    if (cookieName) {
      // store negative answers for only a week
      setCookie(cookieName, newValue, newValue === "no" ? { expires: 7 }: undefined);
    }

    setPilotFeatures(currentFeatures => { currentFeatures[featureSwitch] = newValue });

  }, [ cookieName, featureSwitch, setPilotFeatures ]);

  return {
    pilotFeatureCanBeUsed,
    pilotFeatureSetting: pilotFeatureCanBeUsed ? pilotFeatureValue : "no",
    updatePilotFeatureSetting: pilotFeatureCanBeUsed ? updatePilotFeature : () => {}
  };
}
